import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { posApi } from '@/api/pos.api';
import type { BillingCounter } from '@/api/pos.api';
import { queryKeys, tierOptions } from '@/lib/query';
import { POS_SESSION_KEYS } from '@/utils/posSessionStorage';

const COUNTER_KEY = POS_SESSION_KEYS.COUNTER;
const TERMINAL_KEY = POS_SESSION_KEYS.TERMINAL;
const SHIFT_KEY = POS_SESSION_KEYS.SHIFT;

export interface PosSessionValue {
  counters: BillingCounter[];
  counterId: number | undefined;
  setCounterId: (id: number | undefined) => void;
  terminalId: number | undefined;
  setTerminalId: (id: number | undefined) => void;
  selectedCounter: BillingCounter | undefined;
  activeShiftId: number | undefined;
  setActiveShiftId: (id: number | undefined) => void;
  loading: boolean;
  refreshActiveShift: () => Promise<void>;
}

const PosSessionCtx = createContext<PosSessionValue | null>(null);

function usePosSessionState(storeId: number | undefined): PosSessionValue {
  const qc = useQueryClient();
  const [counterId, setCounterIdState] = useState<number | undefined>(() => {
    const raw = sessionStorage.getItem(COUNTER_KEY);
    const n = raw ? Number(raw) : undefined;
    return n && !Number.isNaN(n) ? n : undefined;
  });
  const [terminalId, setTerminalIdState] = useState<number | undefined>(() => {
    const raw = sessionStorage.getItem(TERMINAL_KEY);
    const n = raw ? Number(raw) : undefined;
    return n && !Number.isNaN(n) ? n : undefined;
  });
  const [activeShiftId, setActiveShiftIdState] = useState<number | undefined>(() => {
    const raw = sessionStorage.getItem(SHIFT_KEY);
    const n = raw ? Number(raw) : undefined;
    return n && !Number.isNaN(n) ? n : undefined;
  });

  const { data: countersPage, isLoading: loading } = useQuery({
    queryKey: queryKeys.pos.counters(storeId ?? 0),
    queryFn: () => posApi.listCounters(storeId!),
    enabled: storeId != null,
    ...tierOptions('operational'),
  });
  const counters = countersPage?.content ?? [];

  const setCounterId = useCallback((id: number | undefined) => {
    setCounterIdState(id);
    if (id) sessionStorage.setItem(COUNTER_KEY, String(id));
    else sessionStorage.removeItem(COUNTER_KEY);
  }, []);

  const setTerminalId = useCallback((id: number | undefined) => {
    setTerminalIdState(id);
    if (id) sessionStorage.setItem(TERMINAL_KEY, String(id));
    else sessionStorage.removeItem(TERMINAL_KEY);
  }, []);

  const setActiveShiftId = useCallback((id: number | undefined) => {
    setActiveShiftIdState(id);
    if (id) sessionStorage.setItem(SHIFT_KEY, String(id));
    else sessionStorage.removeItem(SHIFT_KEY);
  }, []);

  const refreshActiveShift = useCallback(async () => {
    if (!counterId) {
      setActiveShiftId(undefined);
      return;
    }
    await qc.invalidateQueries({ queryKey: queryKeys.pos.activeShiftByCounter(counterId) });
    try {
      const shift = await posApi.getActiveShift(counterId);
      if (shift && shift.shiftStatus !== 'CLOSED') {
        setActiveShiftId(shift.id);
      } else {
        setActiveShiftId(undefined);
      }
    } catch {
      setActiveShiftId(undefined);
    }
  }, [counterId, qc, setActiveShiftId]);

  useEffect(() => {
    if (!storeId) {
      setCounterId(undefined);
      setTerminalId(undefined);
      setActiveShiftId(undefined);
      return;
    }
    if (counters.length === 0) return;
    const savedRaw = sessionStorage.getItem(COUNTER_KEY);
    const savedId = savedRaw ? Number(savedRaw) : undefined;
    const savedCounter =
      savedId && !Number.isNaN(savedId) ? counters.find((c) => c.id === savedId) : undefined;
    if (savedCounter) {
      setCounterId(savedCounter.id);
    } else if (counters.length === 1) {
      setCounterId(counters[0].id);
    } else if (!counterId) {
      setTerminalId(undefined);
      setActiveShiftId(undefined);
    }
  }, [storeId, counters, counterId, setCounterId, setTerminalId, setActiveShiftId]);

  useEffect(() => {
    if (!storeId || !counterId || terminalId) return;
    let deviceId = localStorage.getItem('fattahone_device_id');
    if (!deviceId) {
      deviceId = `web-${crypto.randomUUID()}`;
      localStorage.setItem('fattahone_device_id', deviceId);
    }
    posApi
      .registerTerminal({
        storeId,
        counterId,
        deviceId,
        deviceName: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 80) : 'POS Terminal',
        deviceType: 'DESKTOP',
      })
      .then((t) => setTerminalId(t.id))
      .catch(() => {});
  }, [storeId, counterId, terminalId, setTerminalId]);

  useEffect(() => {
    if (!terminalId) return;
    const tick = () => {
      posApi.heartbeat(terminalId).catch(() => {});
    };
    tick();
    const interval = window.setInterval(tick, 30_000);
    return () => window.clearInterval(interval);
  }, [terminalId]);

  useEffect(() => {
    void refreshActiveShift();
  }, [refreshActiveShift]);

  const selectedCounter = counters.find((c) => c.id === counterId);

  return {
    counters,
    counterId,
    setCounterId,
    terminalId,
    setTerminalId,
    selectedCounter,
    activeShiftId,
    setActiveShiftId,
    loading,
    refreshActiveShift,
  };
}

export function PosSessionProvider({
  storeId,
  children,
}: {
  storeId: number | undefined;
  children: ReactNode;
}) {
  const value = usePosSessionState(storeId);
  return createElement(PosSessionCtx.Provider, { value }, children);
}

export function usePosSession(): PosSessionValue {
  const ctx = useContext(PosSessionCtx);
  if (!ctx) {
    throw new Error('usePosSession requires PosSessionProvider');
  }
  return ctx;
}
